/**
 * The query keys the Documents section shares.
 *
 * Every key names the organization, because switching organization - a Super
 * Admin's switch, or staff viewing another - does not clear the cache: without
 * it one organization's library would show under the other's name (the
 * lesson of `arcKey`). Every key but the upload policy sits under
 * `documentsKey`, so one invalidation after any write refreshes the list, the
 * tabs it carries, an open drawer and the categories together (design.md I7).
 * Spelled once here: a page that spelled its own would leave the others
 * showing what the write replaced.
 */
import type { DocumentsParams } from './useDocumentsParams';

/** Everything a write can change, for one organization. */
export const documentsKey = (organizationId: string | null) =>
  ['documents', organizationId] as const;

/**
 * The upload policy from `GET /config`: types and size limit. Outside
 * `documentsKey`, since no write changes it and it is kept for five minutes.
 */
export const configKey = ['documents-config'] as const;

/** One page of the library, keyed by everything the request carries. */
export const listKey = (organizationId: string | null, params: DocumentsParams) =>
  [...documentsKey(organizationId), 'list', params] as const;

/** One document with its pending replacement, for the drawer. */
export const detailKey = (organizationId: string | null, id: string, range: number) =>
  [...documentsKey(organizationId), 'detail', id, range] as const;

/** The categories with their counts, for the Categories page and the select. */
export const categoriesKey = (organizationId: string | null) =>
  [...documentsKey(organizationId), 'categories'] as const;
